<template>
  <popup-dialog
    :visible="message.item != null"
    class="popup-info"
    :class="message.class"
    :title="message.type"
    :width="image ? 1000 : 700"
    x-alignment="center"
    y-alignment="center"
    @show="onVisible"
  >
    <div class="popup-info__wrapper">
      <img
        v-if="image"
        class="popup-info__image"
        :src="imageData"
        :style="{ objectFit: image.objectFit }"
      />
      <div class="popup-info__content">
        <h2 class="title popup-info__title">
          {{ message.title }}
        </h2>

        <component
          v-if="message.item"
          :is="contentComponent"
          class="popup-info__areas"
          :item="message.item"
          :round="message.round"
        />

        <div class="popup-info__footer">
          <label v-if="skippingAllowed">
            <input v-model="ignored" type="checkbox" />
            {{ $t("labels.hideInThisSimulation") }}
          </label>
          <div v-else-if="webLink">
            <a :href="webLink.url" target="_blank">{{
              webLink.text
            }}</a>
          </div>
          <div v-else></div>
          <button ref="confirmButton" class="button" type="button" @click="hide">
            {{ message.buttonText }}
          </button>
        </div>
      </div>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import PopupDialog from "@core/ui/PopupDialog.vue";

import MomentumContent from "@editor/items/momentum/MomentumSimulationMessage.vue";
import ActionContent from "@editor/items/action/ActionSimulationMessage.vue";
import IncidentContent from "@editor/items/incident/IncidentSimulationMessage.vue";
import RelationContent from "@editor/items/relation/RelationSimulationMessage.vue";
import TextContent from "./TextSimulationMessage.vue";

import { useSimulationMessage } from "@core/ui/simulationMessage.store";
import { useImageStore } from "@editor/items/image/images.store";

const message = useSimulationMessage();

const confirmButton = ref<HTMLButtonElement | null>(null);

const contentComponent = computed(() => {
  switch (message.item?.type) {
    case "momentum":
      return MomentumContent;
    case "action":
      return ActionContent;
    case "incident":
      return IncidentContent;
    case "relation":
      return RelationContent;
    case "text":
      return TextContent;
    case "simulationStart":
      return TextContent;
    case "simulationEnd":
      return TextContent;
  }
  return null;
});

const image = computed(() => {
  if (message.item == null) return null;
  if (!('imageId' in message.item)) return null;
  if (message.item.imageId == null) return null;
  return useImageStore().items[message.item.imageId];
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return useImageStore().mediaData[image.value.id] || undefined;
});

const webLink = computed(() => {
  if (message.item == null) return null;
  if (!('webLink' in message.item)) return null;
  if (message.item.webLink == null) return null;
  return message.item.webLink;
});

const ignored = computed({
  get() {
    if (!message.ignoreGetter) return null;
    return message.ignoreGetter();
  },
  set(value: boolean | null) {
    if (message.ignoreSetter == null) return;
    message.ignoreSetter(!value);
  }
});

const skippingAllowed = computed(() => {
  const item = message.item;
  if (item == null) return true;
  return (
    item.type != "text" &&
    item.type != "simulationStart" &&
    item.type != "simulationEnd"
  );
});
    
function hide() {
  message.hide(true);
}

function onVisible() {
  if (confirmButton.value == null) return;
  confirmButton.value.focus();
}
</script>

<style lang="scss">
.popup-info {
  // width in component
  height: 600px;
  display: flex;
  flex-direction: column;
  &__wrapper {
    height: 100%;
    display: flex;
    overflow: hidden;
  }
  &__image {
    height: 100%;
    width: 35%;
    object-fit: cover;
    padding-left: 2rem;
    padding-bottom: 2rem;
    padding-top: 2rem;
    box-sizing: border-box;
    object-position: center top;
  }
  &__content {
    padding: 2em;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  &__title {
    font-weight: 700;
    padding-left: 0.5rem;
    margin-top: -5px;
  }
  &__areas {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 1em;
    min-height: 0;

    display: grid;
    grid-auto-rows: minmax(2rem, auto);
  }
  &__area {
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }
  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5em;
  }
}
</style>
